import {
  CorporationType,
  Country,
  Director,
  File,
  LegacyDocumentType,
  Prisma,
  WorkRequestType,
} from '@prisma/client';
import { Decimal } from '@prisma/client/runtime';
import { IAddress } from '../address.types';
import { YesNoNotSureOptions } from '../common/enums';
import {
  AdditionalInformationForCorporation,
  CorporationHasDirectorAdditionalInformation,
  CryptoCurrencyActivityEnum,
  DirectorPersonalDetails,
  FundSourcesEnum,
  NextStepsForCompany,
  ReasonsForIncoporatingInSingapore,
  StructureTypes,
} from '../random';
import { WorkRequestResponseType } from '../work-request/types';

export type CorporationForRequestSummary = Omit<
  Prisma.BusinessEntityGetPayload<{
    include: {
      corporationDocuments: {
        include: {
          files: true;
        };
      };
      registeredAgent: {
        include: {
          address: true;
        };
      };
      corporationHasDirector: {
        include: {
          director: {
            include: {
              corporationHasDirector: true;
              panCard: true;
              addressProof: true;
              aadharCard: true;
              bankTransferProof: true;
              dscProof: true;
              governmentId: true;
              mailingAddress: true;
              operatingAddress: true;
            };
          };
        };
      };
    };
  }>,
  'additionalOnboardingInformation | operatingAddress | mailingAddress | personalDetails'
> & {
  additionalOnboardingInformation: AdditionalInformationForCorporation;
  operatingAddress: IAddress;
  mailingAddress: IAddress;
  personalDetails: DirectorPersonalDetails;
};

export type IncorporationRequestDataType = {
  hasOptedForMailingAddress: unknown;
  incorporationCountry?: Country;
  companyLegalName?: string;
  companyLegalName2?: string;
  companyLegalName3?: string;
  usePreReservedName?: boolean;
  companyNameSuffix?: string;
  businessActivity?: string;
  industry?: string;
  customIndustry?: string;
  number?: string;
  email?: string;
  shareCount?: number;
  sharePrice?: number;
  shareRetained?: number;
  companyService?: string;
  hasDsc?: boolean;
  corporationType?: CorporationType;
  hasSingaporeCitizensAsDirectors?: boolean;
  hasOptedForNomineeDirector?: boolean;
  hasCorporateStakeholders?: boolean;
  incorporationJurisdiction: string;
  structure: StructureTypes;
  socialLink: string;
  fiscalMonth: string;
  // needed for sg incorporation
  primarySSICCode?: string;
  isBusinessInvolvedInCryptoCurrency?: boolean;
  isBusinessAcceptingCryptocurrencyPayments?: boolean;
  doesBusinessProvideServicesToCryptoCompanies?: boolean;
  isBusinessManagingCryptocurrencyCoins?: boolean;
  hasLicenseFromMonetaryAuthority?: boolean;
  isBusinessRelatedToExistingEntitiesInBlockChainIndustry?: boolean;
  existingEntitiesHasLicenseWithRegulatoryAuthority?: boolean;
  cryptoCurrencyActivities?: CryptoCurrencyActivityEnum[];
  reasonForIncorporatingInSingapore?: ReasonsForIncoporatingInSingapore[];
  sourceForCompanyFunds?: FundSourcesEnum[];
  supportedCountries?: Country[];
  nextStepsForCompany: NextStepsForCompany;
  otherOptionForNextStep?: string;
  hasOptedForSGMailingAddress?: boolean;
  hasOptedForSGRegisteredAddress?: boolean;
  mailingAddress?: IAddress;
  proposedEntityNameFullform?: string;
  paidByCash?: boolean;
  sicCode?: string;
  companyRegisteredEmailAddress?: string;
  hasOptedForUKMailingAddress?: boolean;
  isTaxpayerInUS?: boolean;
  hasDifferentDateInTheFuture?: boolean;
  hasNoSuchStartSellDate?: boolean;
  companyStartBuySellDate?: string;
  numberOfEmployees?: number;
  planToHireUSEmployeesInFirst12Months?: YesNoNotSureOptions;

  // For Self serve registration
  contractId?: string;
  productId?: string;
  bundleId?: string;
};

export interface FormFieldsResponseType {
  outputFieldsForAccountants: {
    outputDocumentsForCorporation: {
      type: LegacyDocumentType;
      value: File | null;
      isOptional: boolean;
    }[];
    outputDocumentsAndFieldsForDirectors: {
      id: number;
      name: string;
      fields: {
        key: Prisma.DirectorScalarFieldEnum;
        displayName: string;
        value: string | number | Date;
      }[];
      files: {
        type: LegacyDocumentType;
        value: File | null;
        isOptional: boolean;
      }[];
    }[];
    outputFieldsForCorporation: {
      key: Prisma.BusinessEntityScalarFieldEnum;
      displayName: string;
      value: string | number | Date;
      isHidden?: boolean;
    }[];
  };
  inputFieldsForAccountants: {
    inputDocumentsForCorporation: {
      type: LegacyDocumentType;
      value: File | null;
      isOptional: boolean;
    }[];
    inputDocumentsAndFieldsForDirectors: {
      id: number;
      name: string;
      fields: {
        key: Prisma.DirectorScalarFieldEnum;
        displayName: string;
        value: string | number | Date;
      }[];
      files: {
        type: LegacyDocumentType;
        value: File | null;
        isOptional: boolean;
      }[];
    }[];
    inputFieldsForCorporation: {
      key: Prisma.BusinessEntityScalarFieldEnum;
      displayName: string;
      value: string | number | Date;
    }[];
  };
}

export type AccountantLinkResponseType = {
  form: FormFieldsResponseType;
  workRequest: WorkRequestResponseType & {
    requestData: IncorporationRequestDataType;
  };
  corporation: CorporationForRequestSummary | null;
  id: string;
};

export enum RequestSummaryTypeFields {
  BLOCK_VIEW = 'BLOCK_VIEW',
  TEXT_FIELD = 'TEXT_FIELD',
  DIRECTOR_VIEW = 'DIRECTOR_VIEW',
  TABLE_VIEW = 'TABLE_VIEW',
  FILE_FIELD = 'FILE_FIELD',
  READ_ONLY_FIELD = 'READ_ONLY_FIELD',
}

export type SummaryRow = {
  title: string;
  value: string | bigint | number | Decimal | File;
  type: RequestSummaryTypeFields;
  isVisible?: boolean;
};

export type DirectorViewProps = {
  header: string;
  directors: Director[];
  requestType: WorkRequestType;
  accountantLinkId?: string | null;
  enforceShowAllFields?: boolean;
  workRequestId?: string;
  corporationId?: number;
  defaultOpen?: boolean;
  workRequestType?: WorkRequestType;
  token?: string;
};

export type BlockViewProps = {
  header: string;
  data: {
    title: string;
    value: SummaryRow['value'];
    type: RequestSummaryTypeFields;
  }[];
  accountantLinkId?: string | null;
  defaultOpen?: boolean;
};

type DirectorPrisma = Prisma.DirectorGetPayload<{
  include: {
    governmentId: true;
    mailingAddress: true;
    operatingAddress: true;
    panCard: true;
    aadharCard: true;
    addressProof: true;
  };
}>;

export type DirectorType = Omit<
  DirectorPrisma,
  'personalDetails | corporationHasDirector'
> & {
  personalDetails: DirectorPersonalDetails;
  corporationHasDirector: {
    corporationId: number;
    directorId: number;
    additionalDirectorInformation: CorporationHasDirectorAdditionalInformation;
  }[];
};

export type FieldConfig = {
  type: SummaryRow['type'];
  title: SummaryRow['title'];
  isVisible?: boolean;
  getValue: ({
    corp,
    workRequest,
  }: {
    corp: CorporationForRequestSummary;
    workRequest: WorkRequestResponseType;
  }) => string | number | Date | File | null;
};

export type DirectorFieldConfig = {
  type: SummaryRow['type'];
  title: SummaryRow['title'];
  isVisible?: boolean;
  getValue: ({
    workRequest,
    director,
  }: {
    workRequest: WorkRequestResponseType;
    director: DirectorType;
  }) => string | number | Date | File | null;
};

export interface ParsedCorporationInformation {
  companyInfo: {
    header: string;
    data: SummaryRow[];
  };
  directorsInfo: {
    header: string;
    data: DirectorType[];
  };
  equityInfo: {
    header: string;
    data: SummaryRow[];
  };
  stakeholderInfo: {
    header: string;
    columns: string[];
    data: string[][];
  };
  companyRolesInfo: {
    header: string;
    data: SummaryRow[];
  };
}
